import { DefaultButton, DialogFooter, mergeStyleSets } from "@fluentui/react";
import { toast } from "components/FluentToast";
import { FormSpinner } from "components/FormSpinner";
import { Formik } from "formik";
import { useUploadFleetVehicles } from "hooks/data/mutations/useUploadFleetVehicles";
import { delay } from "library/delay";
import Dropzone, { IDropFile } from "pages/fleet-form/Dropzone/Dropzone";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { loadVehiclesActions } from "store/fleetPage";
import { ImportVehiclesInfo } from "types/types";
import * as yup from "yup";
import { setDialog } from "../../../store/fleetForm";

const UploadFleetVehiclesDialog = () => {
  const { t } = useTranslation();
  const { mutate: onUploadVehicles, isLoading: isMutationLoading } =
    useUploadFleetVehicles();
  const [timeoutError, setTimeoutError] = useState(false);
  const [importResults, setImportResults] = useState<ImportVehiclesInfo | null>(
    null
  );

  // Combined loading state for API call
  const isLoading = isMutationLoading;

  const dispatch = useDispatch();
  const { fleetId } = useParams() as any;

  // Reset states when dialog is opened
  React.useEffect(() => {
    setTimeoutError(false);
    setImportResults(null);
  }, []);

  const close = () => {
    // Reload vehicles list when closing after a successful operation
    if (importResults) {
      dispatch(
        loadVehiclesActions.trigger({
          isReset: true,
          shouldDelay: false,
          shouldEmptyVehicles: true,
        })
      );
    }

    dispatch(setDialog({ type: "", isOpen: false }));
  };

  const initialValues = {
    file: null,
  };

  const validationSchema = yup.object().shape({
    file: yup.mixed().required(t("bfm.fleetForm.uploadFile")),
  });

  const classes = getClassNames();

  const onSubmit = (values) => {
    const { file } = values;

    // Use the file directly from Dropzone
    onUploadVehicles(
      {
        fleetId,
        file: file,
      },
      {
        onSuccess: (res) => {
          // Extract import results from response
          const importVehiclesInfo = res?.data
            .importVehiclesInfo as ImportVehiclesInfo;

          if (importVehiclesInfo) {
            setImportResults(importVehiclesInfo);

            // Display success message with import counts
            const totalProcessed =
              importVehiclesInfo.inserted +
              importVehiclesInfo.updated +
              importVehiclesInfo.insertFailed +
              importVehiclesInfo.updateFailed;

            const successCount =
              importVehiclesInfo.inserted + importVehiclesInfo.updated;
            const failureCount =
              importVehiclesInfo.insertFailed + importVehiclesInfo.updateFailed;

            toast.success(
              t("bfm.upload.vehicles.success", {
                totalProcessed,
                successCount,
                failureCount,
              })
            );
          } else {
            // Fallback if no results info
            toast.success(
              t("bfm.upload.vehicles.success", {
                totalProcessed: 0,
                successCount: 0,
                failureCount: 0,
              })
            );
            setImportResults({
              inserted: 0,
              insertFailed: 0,
              updated: 0,
              updateFailed: 0,
            });
          }
        },
        onError: (error: any) => {
          const status = error?.response?.status;
          const responseText = error?.response?.data;

          if (
            status === 500 &&
            typeof responseText === "string" &&
            responseText.includes("500 - The request timed out.")
          ) {
            setTimeoutError(true);
          } else {
            // Handle other errors with a generic message
            toast.error(t("bfm.error", "An error occurred"));
          }
        },
      }
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { setSubmitting }) => {
        onSubmit(values);
        await delay(400);
        setSubmitting(false);
      }}
    >
      {(props) => {
        const { handleSubmit, setFieldValue, values } = props;

        const setFile = (file: IDropFile | IDropFile[]) => {
          setFieldValue("file", file);
        };

        return (
          <>
            <div className={classes.main}>
              <form onSubmit={handleSubmit}>
                <div className={classes.content}>
                  {timeoutError ? (
                    <div className={classes.confirmText}>
                      {t(
                        "bfm.sync.inprogress",
                        "The operation is still in progress. Please wait a few minutes until it finishes."
                      )}
                    </div>
                  ) : importResults ? (
                    <div>
                      <div className={classes.resultsHeader}>
                        {t("bfm.upload.vehicles.results", "Import Results")}
                      </div>
                      <div className={classes.resultsGrid}>
                        <div className={classes.resultRow}>
                          <div className={classes.resultLabel}>
                            {t(
                              "bfm.upload.vehicles.inserted",
                              "Successfully Added:"
                            )}
                          </div>
                          <div className={classes.resultValue}>
                            {importResults.inserted}
                          </div>
                        </div>
                        <div className={classes.resultRow}>
                          <div className={classes.resultLabel}>
                            {t(
                              "bfm.upload.vehicles.insertFailed",
                              "Failed to Add:"
                            )}
                          </div>
                          <div className={classes.resultValue}>
                            {importResults.insertFailed}
                          </div>
                        </div>
                        <div className={classes.resultRow}>
                          <div className={classes.resultLabel}>
                            {t(
                              "bfm.upload.vehicles.updated",
                              "Successfully Updated:"
                            )}
                          </div>
                          <div className={classes.resultValue}>
                            {importResults.updated}
                          </div>
                        </div>
                        <div className={classes.resultRow}>
                          <div className={classes.resultLabel}>
                            {t(
                              "bfm.upload.vehicles.updateFailed",
                              "Failed to Update:"
                            )}
                          </div>
                          <div className={classes.resultValue}>
                            {importResults.updateFailed}
                          </div>
                        </div>
                      </div>
                      <div className={classes.refreshNote}>
                        {t(
                          "bfm.upload.vehicles.refreshNote",
                          "Note: If vehicles don't appear in the list after closing this dialog, please try refreshing the list after a few seconds."
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={classes.confirmText}>
                        {t(
                          "bfm.upload.vehicles.description",
                          "Upload Excel file with vehicles to add to this fleet"
                        )}
                      </div>
                      <div className={classes.dropzoneContainer}>
                        <Dropzone
                          formikProps={props}
                          file={values.file}
                          setFile={setFile}
                          name="file"
                          acceptCsvFiles={false}
                        />
                      </div>
                    </>
                  )}
                </div>

                <DialogFooter className={classes.footer}>
                  {timeoutError || importResults ? (
                    <DefaultButton
                      primary
                      text={t("bfm.close", "Close")}
                      onClick={close}
                      disabled={isLoading}
                    />
                  ) : (
                    <DefaultButton
                      primary
                      text={t("bfm.upload", "Upload")}
                      type="submit"
                      iconProps={{
                        iconName: "Upload",
                      }}
                      disabled={isLoading || !values.file}
                    />
                  )}
                </DialogFooter>

                {isLoading && <FormSpinner />}
              </form>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    main: {},
    content: {
      margin: "20px 0",
    },
    confirmText: {
      width: "100%",
      marginBottom: 20,
      fontSize: 14,
    },
    dropzoneContainer: {
      marginTop: 20,
    },
    footer: {
      marginTop: 30,
    },
    resultsHeader: {
      fontWeight: 600,
      fontSize: 16,
      marginBottom: 16,
    },
    resultsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridGap: "10px",
      marginBottom: 16,
    },
    resultRow: {
      display: "flex",
      alignItems: "center",
    },
    resultLabel: {
      fontWeight: 500,
      marginRight: 8,
    },
    resultValue: {
      fontWeight: 600,
    },
    refreshNote: {
      marginTop: 16,
      padding: 12,
      backgroundColor: "#FFF4CE",
      border: "1px solid #FFB900",
      borderRadius: 2,
      fontSize: 13,
    },
  });

export default UploadFleetVehiclesDialog;
